import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { GET_VEHICLE_DOCUMENTS_KEY, VehicleDocumentsResult } from 'api/document';
import { api } from 'api/api';
import { isNullOrUndefined } from 'global/utils/common';
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RoutesSettings, LAST_REQUEST_ID } from 'config/index';
import { removeFromLocalStorage } from 'global/utils/storage';
import { PdfElement } from 'common/pdf-viewer/pdf-viewer.types';
import { onError } from 'global/utils/response/error-handler';
import { showToastSuccess } from 'global/utils/toast';
import { useModal } from 'state/modal';
import { AttachDocsContent } from 'pages/vehicle-documents/components';
import { isMobile } from 'react-device-detect';
import { useMedia } from 'react-use';

export const useVehicularDocuments = (isVehicularCheckPoint: boolean = false) => {
  const { id } = useParams();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const client = useQueryClient();
  const isWide = useMedia('(max-width: 600px)');
  const navigate = useNavigate();

  useEffect(() => {
    removeFromLocalStorage(LAST_REQUEST_ID);
  }, []);

  const { data, isError, isLoading } = useQuery(
    [GET_VEHICLE_DOCUMENTS_KEY, id],
    () => api.document.getVehicleDocuments({ unique_id: id! }),
    {
      enabled: !isNullOrUndefined(id),
      onSuccess: response => {
        const { error, success } = response.data;

        if (!success) {
          setErrorMessage(error);
        }
      },
    },
  );

  const { mutate: deleteDocMutate } = useMutation(async (docId: string) => api.document.deleteVehicleDocument(docId), {
    onError,
    onSuccess: async () => {
      await client.invalidateQueries([GET_VEHICLE_DOCUMENTS_KEY, id]);
      showToastSuccess('Documento eliminado correctamente.');
    },
  });

  const documents = useMemo(() => {
    if (!data?.data?.result?.documents) {
      return [];
    }

    if (!isVehicularCheckPoint) {
      return data?.data?.result?.documents;
    }

    const hasCheckPointDoc = data.data.result.documents.some(doc => doc.type === 'revision_tecnica');
    const docsTypeAvailable = hasCheckPointDoc
      ? ['padron', 'seguro_obligatorio', 'permiso_circulacion', 'revision_tecnica', 'porta_dox']
      : ['padron', 'homologado', 'seguro_obligatorio', 'permiso_circulacion', 'revision_tecnica', 'porta_dox'];

    return data.data.result.documents.filter(doc => docsTypeAvailable.includes(doc.type) && !doc.canDelete);
  }, [data?.data?.result?.documents]);

  const onDownloadDocuments = useCallback(() => {
    api.document.downloadDocuments(documents, id!);
  }, [documents, id]);

  const onNavigate = useCallback(() => {
    if (isVehicularCheckPoint) {
      navigate(RoutesSettings.vehicleDocuments.replace(':id', id || ''), { replace: true });
      return;
    }

    navigate(RoutesSettings.vehicularCheckPoint.replace(':id', id || ''), { replace: true, state: { id } });
  }, []);

  const onDelete = useCallback(async (doc: PdfElement) => {
    await deleteDocMutate(doc.id);
  }, []);

  const { showModal: openAttachDocsModal } = useModal({
    template: <AttachDocsContent />,
    title: 'Adjuntar documentos',
  });

  const onAttachDocuments = useCallback(() => {
    if (isMobile || isWide) {
      navigate(RoutesSettings.attachDocuments, { state: { isVehicularCheckPoint, uniqueId: id } });
      return;
    }

    openAttachDocsModal({ isVehicularCheckPoint, uniqueId: id });
  }, [isMobile, isWide]);

  return {
    data: {
      ...(data?.data?.result || {}),
      documents,
    } as VehicleDocumentsResult,
    errorMessage,
    id,
    isError,
    isLoading,
    onAttachDocuments,
    onDelete,
    onDownloadDocuments,
    onNavigate,
  };
};
